import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Modal, Skeleton, message } from 'antd';
import { ApiError } from '@/shared/api';
import {
  fetchBudgetCategoriesExpenses,
  type BudgetCategoriesExpensesSummary,
  type BudgetLineAmountRow,
  type BudgetSummaryGridRow,
} from '@/shared/api';
import { formatCurrencyCompact, formatPercent1 } from '@/shared/utils/format';

export interface BudgetSummaryModalProps {
  open: boolean;
  onClose: () => void;
  budgetId?: string;
  /** When the API omits `schoolName`, use profile context (may not match peer budgets until API returns owner). */
  contextSchoolName?: string;
  /** Reporting / budget year label when API `year` is missing (e.g. selected year on school profile). */
  contextYear?: string | number;
}

function fmtCell(n: number | null, currency: 'LBP' | 'USD'): string {
  if (n === null || !Number.isFinite(n)) return '—';
  return formatCurrencyCompact(n, currency);
}

function ThreeColSection({
  titleEn,
  titleAr,
  rows,
}: {
  titleEn: string;
  titleAr: string;
  rows: BudgetLineAmountRow[];
}) {
  if (!rows.length) return null;
  return (
    <section className="rounded-lg border border-slate-200 bg-white ring-1 ring-slate-200/80">
      <div className="border-b border-slate-200 bg-[#f2f2f2] px-3 py-2">
        <h4 className="text-sm font-bold text-slate-900 underline decoration-blue-600" dir="rtl">
          {titleAr}
        </h4>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">{titleEn}</p>
      </div>
      <div className="max-h-[min(38vh,20rem)] overflow-y-auto">
        <table className="w-full border-collapse text-sm" dir="rtl" style={{ fontFamily: 'Arial, sans-serif' }}>
          <thead className="sticky top-0 z-[1] bg-[#f2f2f2]">
            <tr>
              <th className="border border-slate-300 px-2 py-2.5 text-right font-bold text-slate-800">اسم النفقة</th>
              <th className="border border-slate-300 px-2 py-2.5 text-right font-bold text-slate-800">المجموع بالليرة</th>
              <th className="border border-slate-300 px-2 py-2.5 text-right font-bold text-slate-800">المجموع بالدولار</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`${row.name}-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/80'}>
                <td className="border border-slate-300 px-2 py-2.5 align-middle text-right font-medium text-slate-900">
                  {row.name}
                </td>
                <td className="border border-slate-300 px-2 py-2.5 align-middle text-right font-mono tabular-nums text-slate-800">
                  {fmtCell(row.ll, 'LBP')}
                </td>
                <td className="border border-slate-300 px-2 py-2.5 align-middle text-right font-mono tabular-nums text-slate-800">
                  {fmtCell(row.usd, 'USD')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SummaryTable({
  rows,
  extraRow,
  mainCurrency,
}: {
  rows: BudgetSummaryGridRow[];
  extraRow: BudgetSummaryGridRow | null;
  mainCurrency: 'LBP' | 'USD';
}) {
  const all = extraRow ? [...rows, extraRow] : rows;
  const mainCc = mainCurrency === 'USD' ? 'USD' : 'LBP';
  return (
    <section className="rounded-lg border border-slate-200 bg-white ring-1 ring-slate-200/80">
      <div className="border-b border-slate-200 bg-[#f2f2f2] px-3 py-2">
        <h4 className="text-sm font-bold text-slate-900 underline decoration-blue-600" dir="rtl">
          ملخص الموازنة
        </h4>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Budget summary</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-sm" dir="rtl" style={{ fontFamily: 'Arial, sans-serif' }}>
          <thead className="bg-[#f2f2f2]">
            <tr>
              <th className="border border-slate-300 px-2 py-2.5 text-right font-bold text-slate-800">المعيار</th>
              <th className="border border-slate-300 px-2 py-2.5 text-right font-bold text-slate-800">المجموع بالليرة</th>
              <th className="border border-slate-300 px-2 py-2.5 text-right font-bold text-slate-800">المجموع بالدولار</th>
              <th className="border border-slate-300 px-2 py-2.5 text-right font-bold text-slate-800">العملة الرئيسية</th>
              <th className="border border-slate-300 px-2 py-2.5 text-right font-bold text-slate-800">ملاحظات</th>
            </tr>
          </thead>
          <tbody>
            {all.map((row, i) => (
              <tr key={`${row.criteria}-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/80'}>
                <td className="border border-slate-300 px-2 py-2.5 align-middle text-right font-medium text-slate-900">
                  {row.criteria}
                </td>
                <td className="border border-slate-300 px-2 py-2.5 align-middle text-right font-mono tabular-nums">
                  {row.ll === null ? '—' : fmtCell(row.ll, 'LBP')}
                </td>
                <td className="border border-slate-300 px-2 py-2.5 align-middle text-right font-mono tabular-nums">
                  {row.usd === null ? '—' : fmtCell(row.usd, 'USD')}
                </td>
                <td className="border border-slate-300 px-2 py-2.5 align-middle text-right font-mono tabular-nums">
                  {row.main === null
                    ? '—'
                    : row.mainIsPercent
                      ? formatPercent1(row.main)
                      : fmtCell(row.main, mainCc)}
                </td>
                <td
                  className={`border border-slate-300 px-2 py-2.5 align-middle text-right text-xs font-medium ${
                    row.note === 'Fully compliant' ? 'text-emerald-700' : row.note === 'Non-compliant' ? 'text-amber-700' : 'text-slate-700'
                  }`}
                >
                  {row.note ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function parseContextYear(y: string | number | undefined): number | undefined {
  if (y === undefined || y === null) return undefined;
  if (typeof y === 'number' && Number.isFinite(y) && y > 0) return Math.round(y);
  const t = String(y).trim();
  if (!t || !/^\d+$/.test(t)) return undefined;
  const n = parseInt(t, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function BudgetSummaryModal({
  open,
  onClose,
  budgetId,
  contextSchoolName,
  contextYear,
}: BudgetSummaryModalProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<BudgetCategoriesExpensesSummary | null>(null);

  useEffect(() => {
    if (!open || !budgetId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    void (async () => {
      try {
        const summary = await fetchBudgetCategoriesExpenses(budgetId);
        if (!cancelled) setData(summary);
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof ApiError ? ApiError.getUserMessage(e) : 'Could not load budget summary.';
          message.error(msg);
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, budgetId]);

  const modalTitle = useMemo((): ReactNode => {
    const apiSchool = data?.schoolName?.trim();
    const ctxSchool = contextSchoolName?.trim();
    const schoolLine = apiSchool || ctxSchool || 'Budget summary';
    const apiYear = data?.year != null && data.year > 0 ? data.year : undefined;
    const ctxY = parseContextYear(contextYear);
    const yearNum = apiYear ?? ctxY;
    const yearLine = yearNum != null ? `Year ${yearNum}` : 'Year —';
    return (
      <div className="pr-6">
        <div className="text-base font-semibold leading-snug text-slate-900">{schoolLine}</div>
        <div className="mt-0.5 text-sm font-normal leading-snug text-slate-600">{yearLine}</div>
      </div>
    );
  }, [data?.schoolName, data?.year, contextSchoolName, contextYear]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={960}
      title={modalTitle}
      destroyOnClose
      maskClosable
      styles={{ body: { maxHeight: 'min(88vh, 900px)', overflowY: 'auto', paddingTop: 12 } }}
    >
      {!budgetId ? (
        <p className="text-sm text-slate-600">No budget selected.</p>
      ) : loading ? (
        <div className="space-y-4 py-2" aria-busy="true" aria-label="Loading budget summary">
          <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 1 }} />
          <Skeleton active title={false} paragraph={{ rows: 2 }} />
          <Skeleton active title={false} paragraph={{ rows: 8 }} />
        </div>
      ) : data ? (
        <div className="space-y-5 text-sm text-slate-800">
          <ThreeColSection titleEn="Expenses — category A" titleAr="النفقات من الفئة أ" rows={data.categoryA} />
          <ThreeColSection titleEn="Expenses — category B" titleAr="النفقات من الفئة ب" rows={data.categoryB} />
          <ThreeColSection titleEn="Expenses — category C" titleAr="النفقات من الفئة ج" rows={data.categoryC} />
          <ThreeColSection titleEn="Expenses — category D" titleAr="النفقات من الفئة د" rows={data.categoryD} />

          <SummaryTable rows={data.budgetSummary} extraRow={data.tuitionVsLastYear} mainCurrency={data.displayMainCurrency} />
        </div>
      ) : (
        <p className="text-sm text-slate-600">Nothing to show.</p>
      )}
    </Modal>
  );
}
